import React, { useState, useEffect, useCallback } from "react";
import { usePaginated, useAsync } from "../hooks";
import { api } from "../api";
import { fmtDate, fmtMoney, I } from "../utils";
import {
  useToast,
  useConfirm,
  Btn,
  Card,
  Icon,
  Spinner,
  ApiError,
  Modal,
  Badge,
  Pages,
  Inp,
  Sel,
  Txa,
  PieChart,
} from "../ui";

import { BRAND } from "../brand";

// ─── SUPPORTED CURRENCIES ────────────────────────────────────────────────────
const CURRENCIES = [
  { value: "USD", label: "USD – US Dollar" },
  { value: "NGN", label: "NGN – Nigerian Naira" },
  { value: "GBP", label: "GBP – British Pound" },
  { value: "EUR", label: "EUR – Euro" },
  { value: "GHS", label: "GHS – Ghanaian Cedi" },
  { value: "KES", label: "KES – Kenyan Shilling" },
  { value: "ZAR", label: "ZAR – South African Rand" },
  { value: "AED", label: "AED – UAE Dirham" },
  { value: "CAD", label: "CAD – Canadian Dollar" },
  { value: "AUD", label: "AUD – Australian Dollar" },
];

// ─── CURRENCY SWITCHER ────────────────────────────────────────────────────────
// A compact pill-style selector shown in the page header.
function CurrencySwitcher({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-3 py-1.5">
      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 select-none">
        Currency
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer"
      >
        {CURRENCIES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.value}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── FINANCE FORM ─────────────────────────────────────────────────────────────
function FinanceForm({
  initial = {},
  categories = [],
  clients = [],
  defaultCurrency = BRAND.currency,   // pre-select the active dashboard currency
  onSubmit,
  onClose,
  saving,
}) {
  const [f, sf] = useState({
    financeCategory: initial.financeCategory?._id || null,
    type:            initial.type       || "income",
    amount:          initial.amount     || "",
    date:            initial.date ? initial.date.split("T")[0] : "",
    client:          initial.client?._id || null,
    description:     initial.description || "",
    currency:        initial.currency   || defaultCurrency,
    reference:       initial.reference  || "",
  });
  const set = (k) => (e) => sf((p) => ({ ...p, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(f);
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-2 gap-4">
        <Sel
          label="Type *"
          value={f.type}
          onChange={set("type")}
          options={[
            { value: "income",  label: "Income"  },
            { value: "expense", label: "Expense" },
          ]}
        />
        <Sel
          label="Category *"
          value={f.financeCategory}
          onChange={set("financeCategory")}
          required
          options={categories.map((c) => ({
            value: c._id,
            label: c.categoryName,
          }))}
        />
        <Inp
          label="Amount *"
          type="number"
          placeholder="0.00"
          value={f.amount}
          onChange={set("amount")}
          required
        />
        <Inp
          label="Date *"
          type="date"
          value={f.date}
          onChange={set("date")}
          required
        />
        <Sel
          label="Currency"
          value={f.currency}
          onChange={set("currency")}
          options={CURRENCIES}
        />
        <Sel
          label="Client (optional)"
          value={f.client}
          onChange={set("client")}
          options={clients.map((c) => ({ value: c._id, label: c.fullName }))}
        />
      </div>
      <Inp
        label="Reference"
        placeholder="INV-001"
        value={f.reference}
        onChange={set("reference")}
      />
      <Txa
        label="Description"
        placeholder="Details about this transaction…"
        value={f.description}
        onChange={set("description")}
      />
      <div className="flex gap-3 pt-2">
        <Btn type="submit" variant="primary" className="flex-1" loading={saving}>
          Save Record
        </Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}

// ─── SUMMARY BREAKDOWN ────────────────────────────────────────────────────────
// The headline totals combine THREE sources (manual finance records, client trip
// payments, and completed refunds). The transactions table below only lists the
// manual records, so without this panel the cards look like they don't add up.
function SummaryBreakdown({ bd, currency, loading }) {
  const m = (v) => fmtMoney(v ?? 0, currency);
  if (loading) return null;

  const rows = [
    { label: "Finance records (income)", value: bd.financeIncome, sign: "+", tone: "text-emerald-600" },
    { label: "Client trip payments",     value: bd.clientPayments, sign: "+", tone: "text-emerald-600" },
    { label: "Finance records (expense)", value: bd.financeExpenses, sign: "−", tone: "text-rose-500" },
    { label: "Refunds paid out",          value: bd.refunds,         sign: "−", tone: "text-rose-500" },
  ];

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-bold text-slate-700">What's included</h3>
          <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5">
            Every {currency} movement in the CRM
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0"
          >
            <span className="text-sm text-slate-500">{r.label}</span>
            <span className={`text-sm font-bold ${r.tone}`}>
              {r.sign} {m(r.value)}
            </span>
          </div>
        ))}
      </div>

      {/* Kai Credit never leaves the bank — it becomes credit toward a future trip. */}
      {bd.kaiCreditIssued > 0 && (
        <div className="mt-3 flex items-center justify-between px-3 py-2 rounded-lg bg-purple-50 border border-purple-100">
          <span className="text-[11px] font-bold text-purple-600">{`${BRAND.creditName} issued`}<span className="font-normal text-purple-400"> · not counted as cash</span>
          </span>
          <span className="text-xs font-black text-purple-600">
            {m(bd.kaiCreditIssued)}
          </span>
        </div>
      )}
    </Card>
  );
}

// ─── FINANCES TAB ─────────────────────────────────────────────────────────────
export function FinancesTab() {
  const toast = useToast();
  const confirm = useConfirm();

  // ── Active currency — drives EVERYTHING ───────────────────────────────────
  const [activeCurrency, setActiveCurrency] = useState(BRAND.currency);

  // ── Transactions list (currency-filtered, paginated) ──────────────────────
  // Build the path reactively; usePaginated re-fetches when path changes.
  const txPath = `/crm/finances?currency=${activeCurrency}`;
  const {
    data: records,
    total,
    page,
    totalPages,
    loading,
    error,
    setPage,
    refetch: refetchRecords,
  } = usePaginated(txPath);

  // ── Finance summary (currency-filtered) ───────────────────────────────────
  // We use a manual fetch here because the URL changes with currency.
  const [fin, setFin]       = useState(null);
  const [fLoad, setFLoad]   = useState(true);
  const [fError, setFError] = useState(null);

  const fetchSummary = useCallback(async () => {
    setFLoad(true);
    setFError(null);
    try {
      const data = await api.get(
        `/crm/finances/summary?currency=${activeCurrency}`,
      );
      setFin(data);
    } catch (e) {
      setFError(e.message);
    } finally {
      setFLoad(false);
    }
  }, [activeCurrency]);

  // Re-fetch summary whenever currency changes
  useEffect(() => {
    fetchSummary();
    // Also reset to page 1 on currency switch so we don't land on a
    // non-existent page for a currency with fewer records.
    setPage(1);
  }, [activeCurrency]);

  // ── Categories & clients (not currency-dependent) ─────────────────────────
  const { data: cats,    refetch: rCats    } = usePaginated("/crm/finance-categories", 100);
  const { data: clients                    } = usePaginated("/crm/clients", 200);

  // ── Local UI state ────────────────────────────────────────────────────────
  const [filterType, setFT]     = useState("all");
  const [modal, setModal]       = useState(null);
  const [sel, setSel]           = useState(null);
  const [saving, setSaving]     = useState(false);
  const [catName, setCatName]   = useState("");
  const [catType, setCatType]   = useState("income");
  const [catSaving, setCatSav]  = useState(false);

  // ── Helpers: refetch both list and summary together ───────────────────────
  const refetchAll = useCallback(() => {
    refetchRecords();
    fetchSummary();
  }, [refetchRecords, fetchSummary]);

  // ── Type filter applied client-side (records already currency-filtered) ───
  const shown = (records || []).filter(
    (r) => filterType === "all" || r.type === filterType,
  );

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const doCreate = async (f) => {
    setSaving(true);
    try {
      await api.post("/crm/finances", f);
      toast("Record created");
      setModal(null);
      refetchAll();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doEdit = async (f) => {
    setSaving(true);
    try {
      await api.patch(`/crm/finances/${sel._id}`, f);
      toast("Updated");
      setModal(null);
      refetchAll();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    if (!(await confirm({ title: "Delete record?", message: "This finance record will be permanently removed.", confirmText: "Delete record" }))) return;
    try {
      await api.delete(`/crm/finances/${id}`);
      toast("Deleted");
      refetchAll();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const addCat = async () => {
    if (!catName.trim()) return;
    setCatSav(true);
    try {
      await api.post("/crm/finance-categories", {
        categoryName: catName,
        type: catType,
      });
      toast("Category added");
      setCatName("");
      rCats();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setCatSav(false);
    }
  };

  const delCat = async (id) => {
    if (!(await confirm({ title: "Delete category?", message: "This category will be permanently removed.", confirmText: "Delete category" }))) return;
    try {
      await api.delete(`/crm/finance-categories/${id}`);
      toast("Deleted");
      rCats();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  // ── Derived summary values ────────────────────────────────────────────────
  // These now span the WHOLE platform (finance records + client payments −
  // refunds), not just the rows in the table below. See `breakdown`.
  const totalIncome   = fin?.totalIncome   ?? 0;
  const totalExpenses = fin?.totalExpenses ?? 0;
  const grossProfit   = fin?.grossProfit   ?? 0;
  const bd            = fin?.breakdown     ?? {};

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            Finances
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Showing {total ?? "–"} records in{" "}
            <span className="font-bold text-slate-600">{activeCurrency}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Currency switcher — the single source of truth */}
          <CurrencySwitcher
            value={activeCurrency}
            onChange={(c) => {
              setActiveCurrency(c);
              setFT("all"); // reset type filter on currency change
            }}
          />
          <Btn variant="primary" onClick={() => setModal("create")}>
            <Icon d={I.plus} size={16} />
            New Record
          </Btn>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            label: "Total Income",
            value: totalIncome,
            color: "bg-emerald-500",
            icon: "arrowUp",
          },
          {
            label: "Total Expenses",
            value: totalExpenses,
            color: "bg-rose-500",
            icon: "finances",
          },
          {
            label: "Gross Profit",
            value: grossProfit,
            color: grossProfit >= 0 ? "bg-[rgb(var(--brand-rgb))]" : "bg-rose-600",
            icon: "finances",
          },
        ].map((s) => (
          <Card key={s.label} className="p-5 flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}
            >
              <Icon d={I[s.icon]} size={22} className="text-white" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800">
                {fLoad ? (
                  <span className="inline-block w-20 h-5 bg-slate-100 rounded animate-pulse" />
                ) : fError ? (
                  <span className="text-rose-400 text-sm">–</span>
                ) : (
                  fmtMoney(s.value, activeCurrency)
                )}
              </p>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {s.label}
                {/* Small currency tag so the user always knows what they're looking at */}
                <span className="ml-1.5 font-black text-[rgb(var(--brand-rgb))]">
                  {activeCurrency}
                </span>
              </p>
            </div>
          </Card>
        ))}
      </div>

      {/* ── Chart + Breakdown ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-5 flex flex-col items-center">
          <h3 className="font-bold text-slate-700 mb-1 self-start">
            Income vs Expenses
          </h3>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 self-start mb-3">
            {activeCurrency}
          </p>
          {fLoad ? (
            <Spinner />
          ) : (
            <>
              <PieChart income={totalIncome} expenses={totalExpenses} />
              <div className="flex gap-6 text-xs mt-2">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" />
                  Income
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-rose-500 inline-block" />
                  Expense
                </span>
              </div>
              {totalIncome === 0 && totalExpenses === 0 && (
                <p className="text-xs text-slate-400 mt-3 text-center">
                  No {activeCurrency} records yet
                </p>
              )}
            </>
          )}
        </Card>

        <div className="lg:col-span-2">
          <SummaryBreakdown bd={bd} currency={activeCurrency} loading={fLoad} />
        </div>
      </div>

      {/* ── Categories ── */}
      <Card className="p-5">
        <h3 className="font-bold text-slate-700 mb-4">Finance Categories</h3>
        <div className="space-y-2 mb-4 max-h-44 overflow-y-auto">
          {(cats || []).map((c) => (
            <div
              key={c._id}
              className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50"
            >
              <span className="text-sm font-medium text-slate-700">
                {c.categoryName}
              </span>
              <div className="flex items-center gap-2">
                <Badge
                  label={c.type}
                  colorClass={
                    c.type === "income"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-rose-100 text-rose-700"
                  }
                />
                <Btn variant="danger" size="sm" onClick={() => delCat(c._id)}>
                  <Icon d={I.trash} size={12} />
                </Btn>
              </div>
            </div>
          ))}
          {!(cats || []).length && (
            <p className="text-sm text-slate-400 text-center py-3">
              No categories yet
            </p>
          )}
        </div>
        <div className="flex gap-2 pt-3 border-t border-slate-100">
          <input
            value={catName}
            onChange={(e) => setCatName(e.target.value)}
            placeholder="New category name…"
            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)]"
          />
          <select
            value={catType}
            onChange={(e) => setCatType(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)]"
          >
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
          <Btn variant="primary" size="sm" onClick={addCat} loading={catSaving}>
            <Icon d={I.plus} size={14} />
          </Btn>
        </div>
      </Card>

      {/* ── Transactions table ── */}
      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-slate-700">Transactions</h3>
            {/* Be explicit: this table is manual records only — client payments and
                refunds are counted in the totals above but live in their own sections. */}
            <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5">
              {activeCurrency} · manually recorded entries only
            </p>
          </div>
          <div className="flex gap-2">
            {["all", "income", "expense"].map((t) => (
              <button
                key={t}
                onClick={() => setFT(t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${
                  filterType === t
                    ? "bg-[rgb(var(--brand-rgb))] text-white"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center">
            <Spinner />
          </div>
        ) : error ? (
          <ApiError msg={error} onRetry={refetchAll} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  <th className="text-left px-5 py-3">Description</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Client</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-right px-5 py-3">
                    Amount ({activeCurrency})
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {!shown.length && (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center text-slate-400 text-sm py-10"
                    >
                      No {activeCurrency} records
                      {filterType !== "all" ? ` (${filterType})` : ""}
                    </td>
                  </tr>
                )}
                {shown.map((r, i) => (
                  <tr
                    key={r._id}
                    className={`border-t border-slate-50 hover:bg-slate-50 transition-colors ${
                      i % 2 !== 0 ? "bg-slate-50/40" : ""
                    }`}
                  >
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium text-slate-700">
                        {r.description || "–"}
                      </p>
                      {r.reference && (
                        <p className="text-xs text-slate-400">{r.reference}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {r.financeCategory?.categoryName || "–"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {r.client?.fullName || "–"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {fmtDate(r.date)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        label={r.type}
                        colorClass={
                          r.type === "income"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-rose-100 text-rose-700"
                        }
                      />
                    </td>
                    <td
                      className={`px-5 py-3 text-right font-bold text-sm ${
                        r.type === "income"
                          ? "text-emerald-600"
                          : "text-rose-500"
                      }`}
                    >
                      {r.type === "expense" ? "–" : "+"}
                      {fmtMoney(r.amount, r.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSel(r);
                            setModal("edit");
                          }}
                        >
                          <Icon d={I.edit} size={13} />
                        </Btn>
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={() => doDelete(r._id)}
                        >
                          <Icon d={I.trash} size={13} />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-50">
          <Pages
            page={page}
            totalPages={totalPages}
            total={total}
            onPage={setPage}
            loading={loading}
          />
        </div>
      </Card>

      {/* ── Modals ── */}
      {modal === "create" && (
        <Modal title="New Finance Record" onClose={() => setModal(null)} wide>
          <FinanceForm
            categories={cats || []}
            clients={clients || []}
            defaultCurrency={activeCurrency}
            onSubmit={doCreate}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}
      {modal === "edit" && sel && (
        <Modal title="Edit Finance Record" onClose={() => setModal(null)} wide>
          <FinanceForm
            initial={sel}
            categories={cats || []}
            clients={clients || []}
            defaultCurrency={activeCurrency}
            onSubmit={doEdit}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}
    </div>
  );
}